import { useState, type ReactNode } from 'react';
import {
  TL,
  TL_PATIENTEN,
  TlAssistent,
  TlShell,
  TlWrap,
  type TlNav,
} from './therapieland/chrome.js';
import { TlDashboard } from './therapieland/dashboard.js';
import { TlPatienten } from './therapieland/patienten.js';
import { TlProgrammas } from './therapieland/programmas.js';
import { TlVoortgang, TlVragenlijst } from './therapieland/voortgang.js';

/**
 * De nagebouwde Therapieland-omgeving voor de sales-etalage op /e-health.
 *
 * De schermen zijn nagebouwd naar docs/designs/screenshots-therapieland/. Twee
 * modules uit opties-binnen-therapieland.png stonden niet in het account dat
 * gescreenshot kon worden (Chat en Zelfhulpportaal); die tonen een korte
 * toelichting in plaats van een verzonnen scherm. Beeldbellen stond er ook niet
 * in en gebruikt daarom het eerder gebouwde SUPPOHRT-referentieontwerp, dat via
 * de prop binnenkomt.
 */
export function TherapielandEnv({ beeldbellen }: { beeldbellen: ReactNode }): JSX.Element {
  const [nav, setNav] = useState<TlNav>('dashboard');
  const [patientId, setPatientId] = useState<string | null>(null);
  const [programma, setProgramma] = useState<string | null>(null);
  const [inStap, setInStap] = useState(false);

  const patient = TL_PATIENTEN.find((p) => p.id === patientId);

  function ga(n: TlNav): void {
    setNav(n);
    setPatientId(null);
    setProgramma(null);
    setInStap(false);
  }

  return (
    <TlShell nav={nav} setNav={ga}>
      <div className="relative min-h-full">
        {nav === 'dashboard' && <TlDashboard onPatienten={() => ga('patienten')} />}

        {nav === 'patienten' &&
          (programma && patient ? (
            inStap ? (
              <TlVragenlijst onTerug={() => setInStap(false)} />
            ) : (
              <TlVoortgang
                patientNaam={patient.naam}
                programma={programma}
                onTerug={() => setProgramma(null)}
                onStap={() => setInStap(true)}
              />
            )
          ) : (
            <TlPatienten
              patientId={patientId}
              setPatientId={setPatientId}
              onProgramma={setProgramma}
            />
          ))}

        {nav === 'programmas' && <TlProgrammas />}

        {nav === 'beeldbellen' && beeldbellen}

        {nav === 'vragenlijsten' && <TlVragenlijst onTerug={() => ga('patienten')} />}

        {nav === 'monitoring' && (
          <NietInDemo
            titel="Monitoring"
            uitleg="Monitoring zit in het portaal in het dossier van de cliënt, onder het tabblad Monitor. Open een cliënt via Patiënten om het te bekijken."
          />
        )}

        {nav === 'chat' && (
          <NietInDemo
            titel="Chat"
            uitleg="Deze module was niet geactiveerd in het account waarvan de referentiebeelden komen, dus is er geen scherm van nagebouwd."
          />
        )}

        {nav === 'zelfhulp' && (
          <NietInDemo
            titel="Zelfhulpportaal"
            uitleg="Deze module was niet geactiveerd in het account waarvan de referentiebeelden komen, dus is er geen scherm van nagebouwd."
          />
        )}

        <TlAssistent />
      </div>
    </TlShell>
  );
}

function NietInDemo({ titel, uitleg }: { titel: string; uitleg: string }): JSX.Element {
  return (
    <TlWrap>
      <h1 className="text-[34px] font-bold" style={{ color: TL.ink }}>
        {titel}
      </h1>
      <p className="mt-4 max-w-[620px] text-[16px] leading-relaxed" style={{ color: TL.body }}>
        {uitleg}
      </p>
    </TlWrap>
  );
}
